import { gql } from "@apollo/client";

import {
  LOGISTICS_DHL_ACCOUNT_SETTINGS_FIELDS,
  LOGISTICS_DHL_SHIPPING_SETTINGS_FIELDS,
  LOGISTICS_SHIPPING_LABEL_FIELDS,
} from "./fragments";
import {
  CRM_DUPLICATE_CANDIDATE_FIELDS,
  DEVICE_ENROLLMENT_TOKEN_FIELDS,
  MARKETING_DELIVERY_SETTINGS_FIELDS,
  PLACETEL_PHONE_ACCOUNT_FIELDS,
  SALESFORCE_INTEGRATION_FIELDS,
} from "./queries";

export const CREATE_BILLING_INVOICE_DOWNLOAD = gql`
  mutation CreateBillingInvoiceDownload($invoiceId: String!, $organizationId: UUID) {
    createBillingInvoiceDownload(invoiceId: $invoiceId, organizationId: $organizationId) {
      url
      expiresAt
    }
  }
`;

export const SET_ORGANIZATION_BILLING_ACCESS = gql`
  mutation SetOrganizationBillingAccess(
    $organizationId: UUID!
    $accessEnabled: Boolean!
    $expectedMappingVersion: UUID!
  ) {
    setOrganizationBillingAccess(
      organizationId: $organizationId
      accessEnabled: $accessEnabled
      expectedMappingVersion: $expectedMappingVersion
    ) {
      organizationId
      accessEnabled
      mappingConfigured
      invoiceAccountConfigured
      salesforceAccountId
      salesforceAccountName
      frisbiiCustomerId
      mappingVersion
    }
  }
`;

export const SET_ORGANIZATION_BILLING_ACCOUNT = gql`
  mutation SetOrganizationBillingAccount(
    $organizationId: UUID!
    $salesforceAccountId: String!
    $frisbiiCustomerId: String
    $expectedMappingVersion: UUID
    $confirmed: Boolean!
  ) {
    setOrganizationBillingAccount(
      organizationId: $organizationId
      salesforceAccountId: $salesforceAccountId
      frisbiiCustomerId: $frisbiiCustomerId
      expectedMappingVersion: $expectedMappingVersion
      confirmed: $confirmed
    ) {
      organizationId
      accessEnabled
      mappingConfigured
      invoiceAccountConfigured
      salesforceAccountId
      salesforceAccountName
      frisbiiCustomerId
      mappingVersion
    }
  }
`;

export const START_LEARNING_COURSE_MUTATION = gql`
  mutation StartLearningCourse($input: StartLearningCourseInput!) {
    startLearningCourse(input: $input) {
      courseId
      courseVersion
      effect
      startedAt
      payload
      progress {
        enrollmentId
        status
        completedAt
        bestScorePercent
        lessons {
          courseVersion
          lessonId
          completedAt
        }
        quizAttempts {
          id
          courseVersion
          serverScorePercent
          serverPassed
          finishedAt
        }
      }
    }
  }
`;

export const COMPLETE_LEARNING_LESSON_MUTATION = gql`
  mutation CompleteLearningLesson($input: CompleteLearningLessonInput!) {
    completeLearningLesson(input: $input) {
      courseId
      courseVersion
      lessonId
      effect
      completedAt
      progress {
        enrollmentId
        status
        completedAt
        bestScorePercent
        lessons {
          courseVersion
          lessonId
          completedAt
        }
      }
    }
  }
`;

export const SUBMIT_LEARNING_QUIZ_ATTEMPT_MUTATION = gql`
  mutation SubmitLearningQuizAttempt($input: SubmitLearningQuizAttemptInput!) {
    submitLearningQuizAttempt(input: $input) {
      attemptId
      courseId
      courseVersion
      effect
      clientScorePercent
      clientPassed
      serverCorrect
      serverTotal
      serverScorePercent
      serverPassed
      startedAt
      finishedAt
      progress {
        enrollmentId
        status
        completedAt
        bestScorePercent
        quizAttempts {
          id
          courseVersion
          serverScorePercent
          serverPassed
          finishedAt
        }
      }
    }
  }
`;

export const CREATE_LOCATION = gql`
  mutation CreateLocation($input: CreateLocationInput!) {
    createLocation(input: $input) {
      location {
        id
        name
        externalId
        type
        status
        partOfId
        organization {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_LOCATION = gql`
  mutation UpdateLocation($input: UpdateLocationInput!) {
    updateLocation(input: $input) {
      location {
        id
        name
        externalId
        type
        partOfId
      }
    }
  }
`;

export const UPSERT_PATIENT = gql`
  mutation UpsertPatient($input: UpsertPatientInput!) {
    upsertPatient(input: $input) {
      id
    }
  }
`;

export const ADD_ACTIVITY_GOALS = gql`
  mutation AddActivityGoals($activityGoals: [ActivityGoalItem!]!) {
    addActivityGoals(input: { activityGoals: $activityGoals })
  }
`;

export const UPSERT_PRACTITIONER = gql`
  mutation UpsertPractitioner($input: UpsertPractitionerInput!) {
    upsertPractitioner(input: $input) {
      id
      warningCode
    }
  }
`;

export const UPSERT_LOCATION = gql`
  mutation UpsertLocation($input: UpsertLocationInput!) {
    upsertLocation(input: $input) {
      id
    }
  }
`;

export const COMPLETE_ONBOARDING = gql`
  mutation CompleteOnboarding($input: CompleteOnboardingInput!) {
    completeOnboarding(input: $input) {
      locationIds
      patientIds
      practitionerIds
    }
  }
`;

export const RESET_PRACTITIONER_PASSWORD_OTP = gql`
  mutation ResetPractitionerPasswordOTP($input: ResetPractitionerPasswordOTPInput!) {
    resetPractitionerPasswordOTP(input: $input) {
      warningCode
    }
  }
`;

export const REVEAL_PRACTITIONER_PASSWORD_OTP = gql`
  mutation RevealPractitionerPasswordOTP($input: RevealPractitionerPasswordOTPInput!) {
    revealPractitionerPasswordOTP(input: $input) {
      otp
      status
    }
  }
`;

export const UPDATE_IMPORTED_PRACTITIONER_STATUS = gql`
  mutation UpdateImportedPractitionerStatus($input: UpdateImportedPractitionerStatusInput!) {
    updateImportedPractitionerStatus(input: $input) {
      id
      warningCode
    }
  }
`;

export const SETUP_ACCOUNT_PORTAL_ACCESS = gql`
  mutation SetupAccountPortalAccess($salesforceAccountId: String!) {
    setupAccountPortalAccess(salesforceAccountId: $salesforceAccountId)
  }
`;

export const SET_ACCOUNT_AMBULANT = gql`
  mutation SetAccountAmbulant($salesforceAccountId: String!, $ambulant: Boolean!) {
    setAccountAmbulant(salesforceAccountId: $salesforceAccountId, ambulant: $ambulant)
  }
`;

export const INVITE_MANAGER = gql`
  mutation InviteManager($salesforceAccountId: String!, $email: String!) {
    inviteManager(salesforceAccountId: $salesforceAccountId, email: $email)
  }
`;

export const SET_ORGANIZATION_SALESFORCE_ACCOUNT = gql`
  mutation SetOrganizationSalesforceAccount($salesforceAccountId: String!, $organizationId: UUID!) {
    setOrganizationSalesforceAccount(salesforceAccountId: $salesforceAccountId, organizationId: $organizationId)
  }
`;

export const CREATE_TEST_LICENSING_CUSTOMER = gql`
  mutation CreateTestLicensingCustomer($email: String!, $organizationName: String!, $managerName: String!) {
    createTestLicensingCustomer(email: $email, organizationName: $organizationName, managerName: $managerName)
  }
`;

export const CREATE_USER_WITH_VALIDATION_CODE = gql`
  mutation CreateUserWithValidationCode(
    $email: String!
    $name: String!
    $kind: UserType!
    $password: String!
    $code: String!
    $organizationName: String
  ) {
    createUserWithValidationCode(
      email: $email
      name: $name
      kind: $kind
      password: $password
      code: $code
      organizationName: $organizationName
    ) {
      jwtToken
    }
  }
`;

export const SAVE_INTEGRATION_SETTINGS = gql`
  mutation SaveIntegrationSettings(
    $organizationId: UUID!
    $provider: IntegrationProvider!
    $credentials: IntegrationCredentialsInput!
  ) {
    saveIntegrationSettings(organizationId: $organizationId, provider: $provider, credentials: $credentials) {
      organizationId
      hasClientSecret
      provider
      credentials {
        serverUrl
        clientId
        clientSecret
      }
    }
  }
`;

export const UPDATE_PLACETEL_INTEGRATION = gql`
  mutation UpdatePlacetelIntegration($input: PlacetelIntegrationInput!) {
    updatePlacetelIntegration(input: $input) {
      enabled
      organizationId
      clientId
      hasClientSecret
      hasRefreshToken
      hasWebhookSecret
      phoneClientId
      hasPhoneClientSecret
      updatedAt
    }
  }
`;

export const CHECK_PLACETEL_CONNECTION = gql`
  mutation CheckPlacetelConnection {
    checkPlacetelConnection {
      ok
      accountName
      errorCode
      callHistory
    }
  }
`;

export const UPDATE_PLACETEL_HOTLINE_NUMBERS = gql`
  mutation UpdatePlacetelHotlineNumbers($numbers: [String!]!) {
    updatePlacetelHotlineNumbers(numbers: $numbers)
  }
`;

export const UPDATE_PLACETEL_OPENING_HOURS = gql`
  mutation UpdatePlacetelOpeningHours($input: [PlacetelOpeningDayInput!]!) {
    updatePlacetelOpeningHours(input: $input) {
      weekday
      opens
      closes
    }
  }
`;

export const BEGIN_PLACETEL_PHONE_SIGN_IN = gql`
  mutation BeginPlacetelPhoneSignIn($redirectUri: String!) {
    beginPlacetelPhoneSignIn(redirectUri: $redirectUri) {
      authorizationUrl
    }
  }
`;

export const COMPLETE_PLACETEL_PHONE_SIGN_IN = gql`
  mutation CompletePlacetelPhoneSignIn($input: CompletePlacetelPhoneSignInInput!) {
    completePlacetelPhoneSignIn(input: $input) {
      account {
        ...PlacetelPhoneAccountFields
      }
      errorCode
    }
  }
  ${PLACETEL_PHONE_ACCOUNT_FIELDS}
`;

export const DISCONNECT_PLACETEL_PHONE = gql`
  mutation DisconnectPlacetelPhone {
    disconnectPlacetelPhone {
      ...PlacetelPhoneAccountFields
    }
  }
  ${PLACETEL_PHONE_ACCOUNT_FIELDS}
`;

export const ISSUE_PLACETEL_PHONE_ACCESS_TOKEN = gql`
  mutation IssuePlacetelPhoneAccessToken($renew: Boolean) {
    issuePlacetelPhoneAccessToken(renew: $renew) {
      accessToken
      expiresAt
      errorCode
    }
  }
`;

export const RECORD_PLACETEL_CALL_EVENT = gql`
  mutation RecordPlacetelCallEvent($input: PlacetelCallEventInput!) {
    recordPlacetelCallEvent(input: $input) {
      id
      remoteNumber
    }
  }
`;

export const ADD_PLACETEL_CALL_NOTE = gql`
  mutation AddPlacetelCallNote($input: PlacetelCallNoteInput!) {
    addPlacetelCallNote(input: $input) {
      id
      callId
      createdAt
    }
  }
`;

export const SYNC_INTEGRATION_DATA = gql`
  mutation SyncIntegrationData($organizationId: String!) {
    syncIntegrationData(organizationId: $organizationId) {
      organizationId
      id
      userId
      provider
      status
      result
      jobId
      summary
      errorMessage
      startedAt
      completedAt
      duration
    }
  }
`;

export const TEST_INTEGRATION_CONNECTION = gql`
  mutation TestIntegrationConnection($organizationId: UUID!) {
    testIntegrationConnection(organizationId: $organizationId) {
      ok
      provider
      stage
      error
      httpStatus
      locationCount
      durationMs
    }
  }
`;

export const PREVIEW_INTEGRATION_IMPORT = gql`
  mutation PreviewIntegrationImport($organizationId: UUID!) {
    previewIntegrationImport(organizationId: $organizationId) {
      id
      organizationId
      kind
      status
      errorMessage
      summary
    }
  }
`;

export const REQUEST_PASSWORD_RESET_OTP = gql`
  mutation RequestPasswordResetOTP($email: String!) {
    requestPasswordResetOTP(input: { email: $email })
  }
`;

export const RESET_PASSWORD_WITH_VALIDATION_CODE = gql`
  mutation ResetPasswordWithValidationCode($code: String!, $email: String!, $password: String!) {
    resetPasswordWithValidationCode(code: $code, email: $email, password: $password) {
      jwtToken
    }
  }
`;

export const PLAN_RECOMMENDED_ACTIVITY = gql`
  mutation PlanRecommendedActivity($input: PlanRecommendedActivityInput!) {
    planRecommendedActivity(input: $input) {
      procedureId
      sessionId
    }
  }
`;

export const REMOVE_PLANNED_PROCEDURE_ACTIVITY_SESSION = gql`
  mutation RemovePlannedProcedureActivitySession($input: RemovePlannedProcedureActivitySessionInput!) {
    removePlannedProcedureActivitySession(input: $input)
  }
`;

export const SUBMIT_ADMIN_PORTAL_FEEDBACK = gql`
  mutation SubmitAdminPortalFeedback($input: SubmitAdminPortalFeedbackInput!) {
    submitAdminPortalFeedback(input: $input) {
      feedbackId
    }
  }
`;

export const GRANT_ORGANIZATION_LEARNING_COURSE = gql`
  mutation GrantOrganizationLearningCourse($input: OrganizationCourseGrantInput!) {
    grantLearningCourse(input: $input) {
      grant {
        id
        courseId
        active
      }
    }
  }
`;

export const REVOKE_ORGANIZATION_LEARNING_COURSE = gql`
  mutation RevokeOrganizationLearningCourse($input: OrganizationCourseGrantInput!) {
    revokeLearningCourse(input: $input) {
      grant {
        id
        courseId
        active
      }
    }
  }
`;

export const GRANT_ORGANIZATION_LEARNING_PATH = gql`
  mutation GrantOrganizationLearningPath($input: OrganizationPathGrantInput!) {
    grantLearningPath(input: $input) {
      grant {
        id
        learningPathId
        active
      }
    }
  }
`;

export const REVOKE_ORGANIZATION_LEARNING_PATH = gql`
  mutation RevokeOrganizationLearningPath($input: OrganizationPathGrantInput!) {
    revokeLearningPath(input: $input) {
      grant {
        id
        learningPathId
        active
      }
    }
  }
`;

const managerLearningAssignmentFields = gql`
  fragment ManagerLearningAssignmentFields on LearningAssignment {
    id
    organizationId
    userId
    itemKind
    courseId
    learningPathId
    learningPathVersion
    kind
    assignedBy
    assignedAt
    unassignedBy
    unassignedAt
    active
    enrollmentIds
  }
`;

export const ASSIGN_MANAGER_LEARNING_COURSE = gql`
  mutation AssignManagerLearningCourse($input: CourseAssignmentInput!) {
    assignLearningCourse(input: $input) {
      assignment {
        ...ManagerLearningAssignmentFields
      }
    }
  }
  ${managerLearningAssignmentFields}
`;

export const UNASSIGN_MANAGER_LEARNING_COURSE = gql`
  mutation UnassignManagerLearningCourse($input: CourseAssignmentInput!) {
    unassignLearningCourse(input: $input) {
      assignment {
        ...ManagerLearningAssignmentFields
      }
    }
  }
  ${managerLearningAssignmentFields}
`;

export const ASSIGN_MANAGER_LEARNING_PATH = gql`
  mutation AssignManagerLearningPath($input: PathAssignmentInput!) {
    assignLearningPath(input: $input) {
      assignment {
        ...ManagerLearningAssignmentFields
      }
    }
  }
  ${managerLearningAssignmentFields}
`;

export const UNASSIGN_MANAGER_LEARNING_PATH = gql`
  mutation UnassignManagerLearningPath($input: PathAssignmentInput!) {
    unassignLearningPath(input: $input) {
      assignment {
        ...ManagerLearningAssignmentFields
      }
    }
  }
  ${managerLearningAssignmentFields}
`;

export const CREATE_DEVICE_ENROLLMENT_TOKEN = gql`
  mutation CreateDeviceEnrollmentToken($input: CreateDeviceEnrollmentTokenInput!) {
    createDeviceEnrollmentToken(input: $input) {
      token {
        ...DeviceEnrollmentTokenFields
      }
      code
      qrPayload
    }
  }
  ${DEVICE_ENROLLMENT_TOKEN_FIELDS}
`;

export const REVOKE_DEVICE_ENROLLMENT_TOKEN = gql`
  mutation RevokeDeviceEnrollmentToken($id: UUID!) {
    revokeDeviceEnrollmentToken(id: $id) {
      id
      status
      revokedAt
    }
  }
`;

export const UNENROLL_DEVICE = gql`
  mutation UnenrollDevice($id: UUID!) {
    unenrollDevice(id: $id) {
      id
      status
    }
  }
`;

export const SEND_DEVICE_ENROLLMENT_TOKEN_EMAIL = gql`
  mutation SendDeviceEnrollmentTokenEmail($input: SendDeviceEnrollmentTokenEmailInput!) {
    sendDeviceEnrollmentTokenEmail(input: $input) {
      id
      recipientEmail
      queuedAt
      sentAt
      failedAt
      status
      sentBy {
        id
        name
      }
    }
  }
`;

export const START_SALESFORCE_SYNC = gql`
  mutation StartSalesforceSync {
    startSalesforceSync
  }
`;

export const START_FULL_SALESFORCE_SYNC = gql`
  mutation StartFullSalesforceSync {
    startFullSalesforceSync
  }
`;

export const CHECK_SALESFORCE_CONNECTION = gql`
  mutation CheckSalesforceConnection($input: SalesforceConnectionInput!) {
    checkSalesforceConnection(input: $input) {
      ok
      errorCode
      dailyApiRequestsMax
      dailyApiRequestsRemaining
    }
  }
`;

export const UPDATE_SALESFORCE_INTEGRATION = gql`
  mutation UpdateSalesforceIntegration($input: SalesforceIntegrationInput!) {
    updateSalesforceIntegration(input: $input) {
      ...SalesforceIntegrationFields
    }
  }
  ${SALESFORCE_INTEGRATION_FIELDS}
`;

export const UPDATE_MARKETING_DELIVERY_SETTINGS = gql`
  mutation UpdateMarketingDeliverySettings($input: MarketingDeliverySettingsInput!) {
    updateMarketingDeliverySettings(input: $input) {
      ...MarketingDeliverySettingsFields
    }
  }
  ${MARKETING_DELIVERY_SETTINGS_FIELDS}
`;

export const SEND_MARKETING_TEST_EMAIL = gql`
  mutation SendMarketingTestEmail($input: MarketingTestEmailInput!) {
    sendMarketingTestEmail(input: $input) {
      ok
      providerMessageId
      errorCode
    }
  }
`;

export const ADD_STAFF_ROLE = gql`
  mutation AddStaffRole($userId: UUID!, $role: StaffRole!) {
    addStaffRole(userId: $userId, role: $role)
  }
`;

export const REMOVE_STAFF_ROLE = gql`
  mutation RemoveStaffRole($userId: UUID!, $role: StaffRole!) {
    removeStaffRole(userId: $userId, role: $role)
  }
`;

export const INVITE_STAFF = gql`
  mutation InviteStaff($email: String!, $roles: [StaffRole!]!) {
    inviteStaff(email: $email, roles: $roles)
  }
`;

export const CANCEL_STAFF_INVITATION = gql`
  mutation CancelStaffInvitation($id: UUID!) {
    cancelStaffInvitation(id: $id)
  }
`;

export const DISMISS_FINANCE_NOTIFICATION = gql`
  mutation DismissFinanceNotification($id: UUID!, $reason: String!) {
    dismissFinanceNotification(id: $id, reason: $reason) {
      id
      dismissedAt
    }
  }
`;

export const REVIEW_CUSTOMER_SERVICE_CRM_DUPLICATE = gql`
  mutation ReviewCustomerServiceCrmDuplicate($input: CrmDuplicateReviewInput!) {
    reviewCustomerServiceCrmDuplicate(input: $input) {
      ...CrmDuplicateCandidateFields
    }
  }
  ${CRM_DUPLICATE_CANDIDATE_FIELDS}
`;

export const CREATE_ACCOUNT_BILLING_INVOICE_DOWNLOAD = gql`
  mutation CreateAccountBillingInvoiceDownload($salesforceId: String!, $invoiceId: String!) {
    createAccountBillingInvoiceDownload(salesforceId: $salesforceId, invoiceId: $invoiceId) {
      url
      expiresAt
    }
  }
`;

export const UPDATE_LOGISTICS_DHL_SHIPPING_SETTINGS = gql`
  mutation UpdateLogisticsDhlShippingSettings($input: UpdateLogisticsDhlShippingSettingsInput!) {
    updateLogisticsDhlShippingSettings(input: $input) {
      ...LogisticsDhlShippingSettingsFields
    }
  }
  ${LOGISTICS_DHL_SHIPPING_SETTINGS_FIELDS}
`;

export const UPDATE_LOGISTICS_DHL_ACCOUNT_SETTINGS = gql`
  mutation UpdateLogisticsDhlAccountSettings($input: UpdateLogisticsDhlAccountSettingsInput!) {
    updateLogisticsDhlAccountSettings(input: $input) {
      ...LogisticsDhlAccountSettingsFields
    }
  }
  ${LOGISTICS_DHL_ACCOUNT_SETTINGS_FIELDS}
`;

export const CHECK_LOGISTICS_DHL_CONNECTION = gql`
  mutation CheckLogisticsDhlConnection {
    checkLogisticsDhlConnection {
      ok
      failure
      missingSettings
      gateway
      storedReturnReceiverFound
      austrianReturnReceivers {
        receiverId
        shipperCountry
        billingNumber
        name
        street
        houseNumber
        postalCode
        city
      }
    }
  }
`;

export const CREATE_LOGISTICS_SHIPPING_LABEL = gql`
  mutation CreateLogisticsShippingLabel($input: CreateLogisticsShippingLabelInput!) {
    createLogisticsShippingLabel(input: $input) {
      outcome
      refusal
      missingAddressFields
      failure
      messages {
        property
        message
        state
      }
      label {
        ...LogisticsShippingLabelFields
      }
      pilot {
        requestId
        outcome
        refusal
        messages
        salesforceCommandId
      }
    }
  }
  ${LOGISTICS_SHIPPING_LABEL_FIELDS}
`;

export const CANCEL_LOGISTICS_SHIPPING_LABEL = gql`
  mutation CancelLogisticsShippingLabel($input: CancelLogisticsShippingLabelInput!) {
    cancelLogisticsShippingLabel(input: $input) {
      outcome
      refusal
      missingAddressFields
      failure
      messages {
        property
        message
        state
      }
      label {
        ...LogisticsShippingLabelFields
      }
      pilot {
        requestId
        outcome
        refusal
        messages
        salesforceCommandId
      }
    }
  }
  ${LOGISTICS_SHIPPING_LABEL_FIELDS}
`;
